import type {
    GridColDef,
    GridColumnResizeParams,
    GridColumnVisibilityModel,
    GridPinnedColumnFields,
    GridSortModel
} from "@mui/x-data-grid-premium";
import {GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD} from "@mui/x-data-grid-premium";
import {GROUP_KEY} from "../CategorizationTableDataModel.ts";

const PREFIX = 'categorization-table-';
const SETTINGS_KEY = PREFIX + 'column-settings'
const VISIBILITY_KEY = PREFIX + 'visibility';
const VISIBILITY_DEFAULTS_SEEDED_KEY = PREFIX + 'visibility-defaults-seeded';
const PINNING_KEY = PREFIX + 'pinning';
const ORDERING_KEY = PREFIX + 'order';
const SORTING_KEY = PREFIX + 'sorting';

// const STORED_COLUMN_DEFINITION_FIELDS = ['field', 'width',] satisfies (keyof GridColDef)[];
type StoredColumnDefinition = Partial<Pick<GridColDef,
    'width'
>>;

interface CategorizationColumnSettings {
    columns: Record<string, StoredColumnDefinition>;
}

export const getCategorizationColumnSettings = (): CategorizationColumnSettings => {
    return getJSON(SETTINGS_KEY) ?? {columns: {}};
};

export const storeColumnWidthChange = (params: GridColumnResizeParams) => {
    const settings = getCategorizationColumnSettings();
    const {colDef, width} = params;
    const s: StoredColumnDefinition = settings.columns[colDef.field] ?? {};
    s.width = width;
    settings.columns[colDef.field] = s;
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

/**
 * Applies the local column settings to the columns that are found
 */
export function applyLocalCategorizationColumnSettings<T extends GridColDef>(columns: T[], groupBy: boolean): T[] {
    const settings = getCategorizationColumnSettings();
    return columns.map(col => {
        const stored = (groupBy && col.field === GROUP_KEY) ? settings.columns[GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD] : settings.columns[col.field];
        if (stored) {
            if (stored.width) col.width = stored.width;
        }
        return col;
    });
}

export const storeColumnVisibilityModelChange = (model: GridColumnVisibilityModel) => {
    localStorage.setItem(VISIBILITY_KEY, JSON.stringify(model));
}
export const getColumnVisibilityModel: () => GridColumnVisibilityModel = () => {
    return getJSON(VISIBILITY_KEY) ?? {};
}

/**
 * Columns for which `default_visibility` has already been applied to the stored visibility model once.
 *
 * This is tracked separately from the visibility model itself because MuiX represents "column is visible"
 * as the *absence* of a key (e.g. its "Show all"/"Reset" actions delete keys rather than setting `true`).
 * That is indistinguishable from "this column was never given a default", so without this separate marker
 * a default_visibility=false column could never be shown again via those bulk actions.
 */
export const getSeededDefaultVisibilityColumns = (): Set<string> => {
    return new Set(getJSON<string[]>(VISIBILITY_DEFAULTS_SEEDED_KEY) ?? []);
}
export const storeSeededDefaultVisibilityColumns = (seeded: Set<string>) => {
    localStorage.setItem(VISIBILITY_DEFAULTS_SEEDED_KEY, JSON.stringify(Array.from(seeded)));
}


export const storeColumnPinningChange = (pinnedColumns: GridPinnedColumnFields) => {
    localStorage.setItem(PINNING_KEY, JSON.stringify(pinnedColumns));
}
export const getColumnPinning: () => GridPinnedColumnFields = () => {
    return getJSON(PINNING_KEY) ?? {};
}

export const storeSortModelChange = (model: GridSortModel) => {
    localStorage.setItem(SORTING_KEY, JSON.stringify(model));
}
export const getSortModel: () => GridSortModel = () => {
    // Default sorting key should be same as BE's default
    return getJSON(SORTING_KEY) ?? [{field: 'p_spend', sort: 'desc'}];
}


// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getJSON<T = any>(key: string): T | undefined {
    try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : undefined;
    } catch {
        return undefined;
    }
}

export const devopsClearCategorizationTableColumnSettings = () => {
    localStorage.removeItem(SETTINGS_KEY)
    localStorage.removeItem(VISIBILITY_KEY)
    localStorage.removeItem(VISIBILITY_DEFAULTS_SEEDED_KEY)
    localStorage.removeItem(PINNING_KEY)
    localStorage.removeItem(ORDERING_KEY)
    localStorage.removeItem(SORTING_KEY)
}
